import { vec3 } from 'gl-matrix';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { getPreviewColor } from './marginToolbarUtils';
import { usePreviewBase } from '../shared/usePreviewBase';

interface UseMarginPreviewOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
}

export function useMarginPreview({
    environment,
    inputRoi,
}: UseMarginPreviewOptions) {
    const {
        isPreviewing,
        setIsPreviewing,
        abortRef,
        updateView,
        clearPreview,
        cleanup,
    } = usePreviewBase({ environment });

    const handlePreviewSymmetric = useCallback(
        async (marginValue: number) => {
            if (
                !environment?.structureSet ||
                !inputRoi?.texture ||
                !environment.image
            ) {
                return;
            }

            if (marginValue === 0) {
                clearPreview();
                return;
            }

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setIsPreviewing(true);

            try {
                const marginTexture = await SDFOperations.applySymmetricMargin(
                    inputRoi.texture,
                    marginValue
                );

                if (controller.signal.aborted) {
                    marginTexture.delete();
                    return;
                }

                const previewTexture = ContourTexture.createNewWithMargin(
                    marginValue,
                    inputRoi.texture
                );
                const previewObj = new CustomTargetObject(
                    environment.image,
                    previewTexture,
                    getPreviewColor(inputRoi.color)
                );

                previewObj.renderOptions = { fillAlpha: 0.18 };
                previewObj.contourTexture = marginTexture;
                environment.operationPreviewStructure = previewObj;
                updateView();
            } catch (e) {
                console.error('Symmetric preview error:', e);
                enqueueSnackbar(`Preview failed: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                setIsPreviewing(false);
            }
        },
        [
            environment,
            inputRoi,
            abortRef,
            clearPreview,
            setIsPreviewing,
            updateView,
        ]
    );

    const handlePreviewAsymmetric = useCallback(
        async (negLpsMm: vec3, posLpsMm: vec3) => {
            if (
                !environment?.structureSet ||
                !inputRoi?.texture ||
                !environment.image
            ) {
                return;
            }

            if (vec3.length(negLpsMm) === 0 && vec3.length(posLpsMm) === 0) {
                clearPreview();
                setIsPreviewing(false);
                return;
            }

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setIsPreviewing(true);

            const maxMarginMm = Math.max(
                ...[...negLpsMm, ...posLpsMm].map(Math.abs)
            );
            const previewTexture = ContourTexture.createNewWithMargin(
                maxMarginMm,
                inputRoi.texture
            );
            const previewObj = new CustomTargetObject(
                environment.image,
                previewTexture,
                getPreviewColor(inputRoi.color)
            );
            previewObj.renderOptions = { fillAlpha: 0.18 };
            environment.operationPreviewStructure = previewObj;
            // TODO: improve these kind of hacky rendering solutions should be removed and
            // heavy computation should be lifted away from blocking the main thread for smooth rendering
            await waitForUI();
            try {
                const marginTexture = await SDFOperations.applyAsymmetricMargin(
                    inputRoi.texture,
                    negLpsMm,
                    posLpsMm
                );

                // A newer preview started (or cleanup ran) while the margin
                // was computing — discard the stale result to avoid a GPU
                // texture leak and avoid overwriting the current preview.
                if (controller.signal.aborted) {
                    marginTexture.delete();
                    return;
                }

                // Replace the placeholder texture and delete it to free GPU
                // resources; CustomTargetObject.contourTexture setter does
                // not dispose the previous value.
                const placeholder = previewObj.contourTexture;
                previewObj.contourTexture = marginTexture;
                placeholder?.delete();

                updateView();
            } catch (e) {
                console.error('Asymmetric preview error:', e);
                clearPreview();
                enqueueSnackbar(
                    `Margin preview failed: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            } finally {
                setIsPreviewing(false);
            }
        },
        [
            environment,
            inputRoi,
            abortRef,
            setIsPreviewing,
            clearPreview,
            updateView,
        ]
    );

    return {
        isPreviewing,
        handlePreviewSymmetric,
        handlePreviewAsymmetric,
        clearPreview,
        cleanup,
        updateView,
    };
}
